import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/authStore';
import { MessageSquare, Send, User, Clock, CheckCheck, Paperclip, Plus } from 'lucide-react';

export const ClientMessagesPage: React.FC = () => {
  const { user } = useAuth();
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [messageContent, setMessageContent] = useState('');
  const [newThreadTitle, setNewThreadTitle] = useState('');
  const [showNewThreadModal, setShowNewThreadModal] = useState(false);

  const queryClient = useQueryClient();

  const { data: threadsRes, isLoading } = useQuery({
    queryKey: ['client-messages'],
    queryFn: () => api.get('/client/messages').then((res) => res.data.data),
  });

  const threads = threadsRes || [];
  const activeThread = threads.find((t: any) => t.id === selectedThreadId) || threads[0] || null;

  const sendMessageMutation = useMutation({
    mutationFn: async ({ threadId, content, title }: { threadId?: string; content: string; title?: string }) => {
      return api.post('/client/messages', { threadId, content, title });
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['client-messages'] });
      setMessageContent('');
      if (res.data?.data?.thread?.id) {
        setSelectedThreadId(res.data.data.thread.id);
      }
      setShowNewThreadModal(false);
      setNewThreadTitle('');
    },
  });

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageContent.trim() || !activeThread) return;
    sendMessageMutation.mutate({
      threadId: activeThread.id,
      content: messageContent.trim(),
    });
  };

  const handleCreateThread = (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageContent.trim()) return;
    sendMessageMutation.mutate({
      title: newThreadTitle.trim() || 'Publishing Inquiry',
      content: messageContent.trim(),
    });
  };

  return (
    <div className="space-y-6">
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-serif font-bold text-slate-900">
            Editorial Communications
          </h1>
          <p className="text-xs sm:text-sm text-slate-500">
            Direct, confidential correspondence with your assigned publishing staff and editorial directors.
          </p>
        </div>

        <button
          onClick={() => setShowNewThreadModal(true)}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white font-bold text-xs transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>New Discussion</span>
        </button>
      </div>

      {/* Main Messaging Interface */}
      {isLoading ? (
        <div className="p-12 text-center text-xs font-mono text-slate-500">Loading messages...</div>
      ) : threads.length === 0 ? (
        <div className="p-12 rounded-2xl bg-white border border-editorial-border text-center space-y-4">
          <MessageSquare className="w-12 h-12 text-slate-300 mx-auto" />
          <h3 className="font-serif text-lg font-bold text-slate-800">No Conversations Yet</h3>
          <p className="text-xs text-slate-500 max-w-sm mx-auto">
            Start a message thread with the editorial department to discuss manuscript revisions, deadlines, or design queries.
          </p>
          <button
            onClick={() => setShowNewThreadModal(true)}
            className="px-5 py-2.5 rounded-lg bg-brand-600 text-white font-bold text-xs"
          >
            Start First Discussion
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 bg-white rounded-2xl border border-editorial-border shadow-xs overflow-hidden min-h-[600px]">
          
          {/* Thread List Sidebar */}
          <div className="lg:col-span-4 border-r border-slate-100 p-4 space-y-2 bg-slate-50/50">
            <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-bold px-2 block">
              Active Conversations ({threads.length})
            </span>
            <div className="space-y-1">
              {threads.map((thr: any) => {
                const isSelected = activeThread?.id === thr.id;
                return (
                  <div
                    key={thr.id}
                    onClick={() => setSelectedThreadId(thr.id)}
                    className={`p-3.5 rounded-xl cursor-pointer transition-all ${
                      isSelected
                        ? 'bg-white shadow-xs border border-brand-300 ring-1 ring-brand-300'
                        : 'hover:bg-white/80 border border-transparent'
                    }`}
                  >
                    <div className="flex items-center justify-between text-xs font-serif font-bold text-slate-900 truncate">
                      <span>{thr.title}</span>
                      <span className="text-[10px] font-mono text-slate-400 font-normal">
                        {new Date(thr.lastMessageAt).toLocaleDateString()}
                      </span>
                    </div>
                    {thr.project && (
                      <span className="text-[10px] font-mono text-brand-700 font-semibold block mt-0.5">
                        {thr.project.projectCode}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Active Thread Chat Area */}
          <div className="lg:col-span-8 flex flex-col justify-between p-6">
            
            {/* Conversation Header */}
            <div className="border-b border-slate-100 pb-4 flex items-center justify-between">
              <div>
                <h3 className="font-serif text-lg font-bold text-slate-900">{activeThread?.title}</h3>
                <p className="text-[11px] text-slate-500 font-mono">
                  Participants: {user?.firstName} {user?.lastName} (Author) &bull; Editorial Committee
                </p>
              </div>
            </div>

            {/* Messages Stream */}
            <div className="flex-1 overflow-y-auto py-6 space-y-4 max-h-[420px] pr-2">
              {activeThread?.messages?.map((msg: any) => {
                const isMe = msg.senderId === user?.id;
                return (
                  <div
                    key={msg.id}
                    className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}
                  >
                    <div
                      className={`max-w-md p-4 rounded-2xl text-xs sm:text-sm leading-relaxed shadow-xs ${
                        isMe
                          ? 'bg-editorial-dark text-white rounded-br-none'
                          : 'bg-editorial-parchment text-slate-900 border border-editorial-border rounded-bl-none'
                      }`}
                    >
                      <span className={`text-[10px] font-bold block mb-1 font-mono ${isMe ? 'text-brand-300' : 'text-brand-700'}`}>
                        {isMe ? 'You' : 'Senior Editor'}
                      </span>
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                    </div>
                    <span className="text-[10px] text-slate-400 font-mono mt-1 px-1">
                      {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Message Input Box */}
            <form onSubmit={handleSendMessage} className="pt-4 border-t border-slate-100 flex items-center gap-2">
              <input
                type="text"
                value={messageContent}
                onChange={(e) => setMessageContent(e.target.value)}
                placeholder="Type your response to the editorial staff..."
                className="flex-1 px-4 py-3 rounded-xl border border-slate-300 text-xs focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
              />
              <button
                type="submit"
                disabled={!messageContent.trim() || sendMessageMutation.isPending}
                className="p-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-bold text-xs disabled:opacity-50 transition-colors shrink-0"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>

          </div>

        </div>
      )}

      {/* New Thread Modal */}
      {showNewThreadModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 sm:p-8 space-y-6 shadow-2xl border border-slate-200">
            <h3 className="font-serif text-xl font-bold text-slate-900">Start New Discussion</h3>

            <form onSubmit={handleCreateThread} className="space-y-4 text-xs">
              <div>
                <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Discussion Subject *
                </label>
                <input
                  type="text"
                  value={newThreadTitle}
                  onChange={(e) => setNewThreadTitle(e.target.value)}
                  placeholder="e.g. Chapter 3 Expository Notes Query"
                  className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
                  required
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Initial Message *
                </label>
                <textarea
                  rows={4}
                  value={messageContent}
                  onChange={(e) => setMessageContent(e.target.value)}
                  placeholder="Write your note to the editorial team..."
                  className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
                  required
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowNewThreadModal(false)}
                  className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={sendMessageMutation.isPending}
                  className="px-5 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white font-bold"
                >
                  Send Discussion
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};
